import React from 'react';
import { EventSnapshot } from '../../../api/types';
import { useTranslation } from '../../../i18n';
import { getCountryFlagUrl } from '../../../utils/flagUtils';
import { translateMarketFilterLabel } from '../../../utils/marketTranslations';

// Merged catalog events are not pre-sorted; order by start time so tournament
// blocks, day groups, and the matches inside them read chronologically.
function eventStartMs(ev: EventSnapshot): number {
  const t = ev.event?.scheduled_at ?? ev.event?.start_time;
  const n = t ? new Date(t).getTime() : NaN;
  return Number.isNaN(n) ? Number.POSITIVE_INFINITY : n;
}

interface CategoryMatchListProps {
  events: EventSnapshot[];
  topMarket1: string | number;
  setTopMarket1: (m: string | number) => void;
  topMarket2: string | number;
  setTopMarket2: (m: string | number) => void;
  topMarket3: string | number;
  setTopMarket3: (m: string | number) => void;
  topMarket4: string | number;
  setTopMarket4: (m: string | number) => void;
  topMarket5: string | number;
  setTopMarket5: (m: string | number) => void;
  openDropdown: string | null;
  setOpenDropdown: (val: string | null | ((prev: string | null) => string | null)) => void;
  options: (string | number)[];
  renderMarketColHeader: (type: string | number, width: string) => React.ReactNode;
  renderMarketColCells: (ev: EventSnapshot, type: string | number, width: string, isUp?: boolean, isDown?: boolean) => React.ReactNode;
  handleEventClick: (ev: EventSnapshot) => void;
  formatTime: (iso: string) => string;
  categoryPrefix?: string;
  hasTabs?: boolean;
  hideSecondMarket?: boolean;
}

export const CategoryMatchList: React.FC<CategoryMatchListProps> = ({
  events,
  categoryPrefix = '',
  hasTabs = false,
  topMarket1,
  setTopMarket1,
  topMarket2,
  setTopMarket2,
  topMarket3,
  setTopMarket3,
  topMarket4,
  setTopMarket4,
  topMarket5,
  setTopMarket5,
  openDropdown,
  setOpenDropdown,
  options,
  renderMarketColHeader,
  renderMarketColCells,
  handleEventClick,
  formatTime,
  hideSecondMarket = false,
}) => {
  const { locale } = useTranslation();

  const { t } = useTranslation();

  if (events.length === 0) return null;

  // Group by tournament (chronologically ordered so day groups and matches
  // within each tournament follow start time)
  const sortedEvents = [...events].sort((a, b) => eventStartMs(a) - eventStartMs(b));
  const byTournament = sortedEvents.reduce((acc, ev) => {
    const t = ev.event.tournament_name;
    if (!acc[t]) acc[t] = [];
    acc[t].push(ev);
    return acc;
  }, {} as Record<string, EventSnapshot[]>);

  return (
    <div className="mb-6">
      <div className="bg-white rounded shadow-sm border border-gray-200 relative z-10">
        
        {/* Tabs (Optional) */}
        {hasTabs && (
          <div className="flex items-center px-4 pt-2 border-b border-gray-200">
            <button className="flex flex-col items-center justify-center min-w-[70px] pb-2 border-b-2 border-[#cc0000] text-gray-900 mx-2">
              <span className="text-[18px] text-[#cc0000] font-black">＋</span>
              <span className="text-[13px] font-bold mt-1">Matches</span>
            </button>
            <button className="flex flex-col items-center justify-center min-w-[90px] pb-2 border-b-2 border-transparent text-gray-600 hover:text-gray-900 mx-2">
              <span className="text-[18px] font-black italic">N<span>+</span></span>
              <span className="text-[13px] font-bold mt-1">MatchKombi+</span>
            </button>
            <button className="flex flex-col items-center justify-center min-w-[70px] pb-2 border-b-2 border-transparent text-gray-600 hover:text-gray-900 mx-2">
              <span className="text-[18px]">📅</span>
              <span className="text-[13px] font-bold mt-1">Outrights</span>
            </button>
            <button className="flex flex-col items-center justify-center min-w-[70px] pb-2 border-b-2 border-transparent text-gray-600 hover:text-gray-900 mx-2">
              <span className="text-[18px]">📊</span>
              <span className="text-[13px] font-bold mt-1">Statistics</span>
            </button>
          </div>
        )}

        {/* Header Row */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 sticky top-[56px] z-20 bg-white">
          <div className="flex items-center gap-2">
            {!hasTabs && (
              <>
                <span className="text-[18px]">⚽</span>
                <h2 className="text-[18px] font-bold text-gray-900 ml-1">{t('sport.football')}</h2>
              </>
            )}
          </div>

          {/* Same geometry as the label row below: [mx-1 w-151]×5 + w-52 spacer,
              so each dropdown sits exactly over its odds column. */}
          <div className="flex items-center">
            <div className="relative mx-1 flex-shrink-0">
              <div onClick={() => setOpenDropdown(o => o === 'c1' ? null : 'c1')} className="w-[151px] bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
                 <span className="text-[14px] font-bold text-gray-700">{translateMarketFilterLabel(topMarket1, locale)}</span>
                 <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </div>
              {openDropdown === 'c1' && (
                <div className="absolute top-full right-0 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
                  <div className="px-3 py-1.5 text-[14px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">{translateMarketFilterLabel('Tipp', locale)}</div>
                  {options.map(opt => (
                    <button key={opt} onClick={() => { setTopMarket1(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[14px] hover:bg-gray-100 flex items-center gap-2">
                      <div className="w-4">{topMarket1 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                      <span className={topMarket1 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{translateMarketFilterLabel(opt, locale)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className={`relative mx-1 flex-shrink-0${hideSecondMarket ? ' hidden min-[1500px]:block' : ''}`}>
              <div onClick={() => setOpenDropdown(o => o === 'c2' ? null : 'c2')} className="w-[151px] bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
                 <span className="text-[14px] font-bold text-gray-700">{translateMarketFilterLabel(topMarket2, locale)}</span>
                 <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </div>
              {openDropdown === 'c2' && (
                <div className="absolute top-full right-0 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
                  <div className="px-3 py-1.5 text-[14px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">{translateMarketFilterLabel('Tipp', locale)}</div>
                  {options.map(opt => (
                    <button key={opt} onClick={() => { setTopMarket2(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[14px] hover:bg-gray-100 flex items-center gap-2">
                      <div className="w-4">{topMarket2 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                      <span className={topMarket2 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{translateMarketFilterLabel(opt, locale)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className={`relative mx-1 flex-shrink-0 ${hideSecondMarket ? 'hidden min-[1650px]:block' : 'hidden min-[1500px]:block'}`}>
              <div onClick={() => setOpenDropdown(o => o === 'c3' ? null : 'c3')} className="w-[151px] bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
                 <span className="text-[14px] font-bold text-gray-700">{translateMarketFilterLabel(topMarket3, locale)}</span>
                 <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </div>
              {openDropdown === 'c3' && (
                <div className="absolute top-full right-0 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
                  <div className="px-3 py-1.5 text-[14px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">{translateMarketFilterLabel('Tipp', locale)}</div>
                  {options.map(opt => (
                    <button key={opt} onClick={() => { setTopMarket3(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[14px] hover:bg-gray-100 flex items-center gap-2">
                      <div className="w-4">{topMarket3 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                      <span className={topMarket3 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{translateMarketFilterLabel(opt, locale)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {!hideSecondMarket && (
              <div className="relative mx-1 flex-shrink-0 hidden min-[1650px]:block">
                <div onClick={() => setOpenDropdown(o => o === 'c4' ? null : 'c4')} className="w-[151px] bg-white border border-gray-200 rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
                   <span className="text-[14px] font-bold text-gray-700">{translateMarketFilterLabel(topMarket4, locale)}</span>
                   <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </div>
                {openDropdown === 'c4' && (
                  <div className="absolute top-full right-0 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
                    <div className="px-3 py-1.5 text-[14px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">{translateMarketFilterLabel('Tipp', locale)}</div>
                    {options.map(opt => (
                      <button key={opt} onClick={() => { setTopMarket4(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[14px] hover:bg-gray-100 flex items-center gap-2">
                        <div className="w-4">{topMarket4 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                        <span className={topMarket4 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{translateMarketFilterLabel(opt, locale)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {!hideSecondMarket && (
                <div className="relative mx-1 flex-shrink-0 hidden min-[1750px]:block">
                  <div onClick={() => setOpenDropdown(o => o === `cat5-${categoryPrefix}` ? null : `cat5-${categoryPrefix}`)} className="w-[151px] bg-[#f0f2f5] rounded px-2 py-1.5 flex items-center justify-between cursor-pointer">
                    <span className="text-[14px] font-bold text-gray-700">{translateMarketFilterLabel(topMarket5, locale)}</span>
                    <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </div>
                  {openDropdown === `cat5-${categoryPrefix}` && (
                    <div className="absolute top-full right-1 mt-1 w-[240px] bg-white border border-gray-200 shadow-xl rounded py-1 z-50">
                      <div className="px-3 py-1.5 text-[14px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1">{translateMarketFilterLabel('Markt Wählen', locale)}</div>
                      {options.map(opt => (
                        <button key={opt} onClick={() => { setTopMarket5(opt); setOpenDropdown(null); }} className="w-full text-left px-3 py-1.5 text-[14px] hover:bg-gray-100 flex items-center gap-2">
                          <div className="w-4">{topMarket5 === opt && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>}</div>
                          <span className={topMarket5 === opt ? 'text-gray-900 font-bold' : 'text-gray-700'}>{translateMarketFilterLabel(opt, locale)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
            )}
            {/* Mirrors the w-[52px] stats column so dropdowns align with the columns. */}
            <div className="w-[52px]" />
          </div>
        </div>

        {/* Tournaments */}
        {Object.entries(byTournament).map(([tournament, eventsGroup]) => {
          
          // Group inside tournament by Day
          const byDay = eventsGroup.reduce((acc, ev) => {
            const starts = ev.event?.scheduled_at ?? ev.event?.start_time;
            const d = starts ? new Date(starts) : null;
            const now = new Date();
            const tomorrow = new Date(now);
            tomorrow.setDate(now.getDate() + 1);
            let dayLabel = d ? d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) : '';
            if (d?.toDateString() === now.toDateString()) dayLabel = 'Today';
            else if (d?.toDateString() === tomorrow.toDateString()) dayLabel = `Tomorrow, ${d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}`;
            
            if (!acc[dayLabel]) acc[dayLabel] = [];
            acc[dayLabel].push(ev);
            return acc;
          }, {} as Record<string, EventSnapshot[]>);

          return (
            <React.Fragment key={tournament}>
              {/* Tournament Title Row */}
              <div className="px-5 py-2.5 bg-white flex items-center flex-wrap justify-between border-b border-gray-100 min-h-[38px]">
                <div className="flex items-center gap-2">
                  {(() => { const flagUrl = getCountryFlagUrl(eventsGroup[0]?.event?.country_code); return flagUrl ? <img src={flagUrl} alt="" className="w-[18px] h-[13px] object-cover flex-shrink-0 rounded-[4px]" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} /> : null; })()}
                  <span className="text-[14px] font-bold text-gray-900 cursor-pointer hover:underline">{categoryPrefix}{tournament}</span>
                  {categoryPrefix === 'Germany / ' && <span className="text-gray-400 font-bold mx-1">›</span>}
                  {categoryPrefix === 'Germany / ' && <svg className="w-4 h-4 text-gray-400 cursor-pointer hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>}
                </div>
                <div className="flex items-center text-[13px] font-bold text-gray-400">
                  {renderMarketColHeader(topMarket1, "w-[151px]")}
                  <div className={`flex-shrink-0${hideSecondMarket ? ' hidden min-[1500px]:block' : ''}`}>{renderMarketColHeader(topMarket2, "w-[151px]")}</div>
                  <div className={`flex-shrink-0 ${hideSecondMarket ? 'hidden min-[1650px]:block' : 'hidden min-[1500px]:block'}`}>{renderMarketColHeader(topMarket3, "w-[151px]")}</div>
                  {!hideSecondMarket && <div className="hidden min-[1650px]:block flex-shrink-0">{renderMarketColHeader(topMarket4, "w-[151px]")}</div>}
                  {!hideSecondMarket && <div className="hidden min-[1750px]:block flex-shrink-0">{renderMarketColHeader(topMarket5, "w-[151px]")}</div>}
                  <div className="w-[52px]"></div>
                </div>
              </div>

              {Object.entries(byDay).map(([dayLabel, dayEvents]) => (
                <React.Fragment key={dayLabel}>
                  {/* Day separator */}
                  <div className="px-5 py-2 bg-white text-[13px] font-black text-gray-900 border-b border-gray-100">
                    {dayLabel}
                  </div>

                  {dayEvents.map((ev) => {
                    const total = ev.markets?.length ?? 0;
                    const starts = ev.event?.scheduled_at ?? ev.event?.start_time;
                    const d = starts ? new Date(starts) : null;
                    const timeStr = d ? formatTime(d.toISOString()) : '';

                    return (
                      <div key={ev.event_id} className="px-5 py-2.5 flex items-center hover:bg-gray-50 transition border-b border-gray-100 bg-white last:border-b-0">
                        
                        <div className="w-[45px] flex-shrink-0 cursor-pointer text-left" onClick={() => handleEventClick(ev)}>
                          <div className="text-[13px] text-gray-500">{timeStr}</div>
                        </div>

                        <div className="flex-1 min-w-0 flex flex-col justify-center cursor-pointer gap-1" onClick={() => handleEventClick(ev)}>
                          <div className="flex items-center gap-2 min-w-0">
                            {ev.event.home.crest_url ? <img src={ev.event.home.crest_url} alt="crest" className="w-[18px] h-[18px] object-contain flex-shrink-0 rounded-[4px]" /> : <img src="/default-crest.svg" alt="crest" className="w-[18px] h-[18px] object-contain flex-shrink-0 rounded-[4px]" />}
                            <span className="text-[15px] font-medium text-gray-900 truncate">{ev.event.home.name}</span>
                          </div>
                          <div className="flex items-center gap-2 min-w-0">
                            {ev.event.away.crest_url ? <img src={ev.event.away.crest_url} alt="crest" className="w-[18px] h-[18px] object-contain flex-shrink-0 rounded-[4px]" /> : <img src="/default-crest.svg" alt="crest" className="w-[18px] h-[18px] object-contain flex-shrink-0 rounded-[4px]" />}
                            <span className="text-[15px] font-medium text-gray-900 truncate">{ev.event.away.name}</span>
                          </div>
                        </div>

                        {/* Dropdown 1 odds */}
                        {renderMarketColCells(ev, topMarket1, "w-[151px]")}
                        
                        {/* Dropdown 2 odds */}
                        <div className={`flex-shrink-0${hideSecondMarket ? ' hidden min-[1500px]:block' : ''}`}>
                          {renderMarketColCells(ev, topMarket2, "w-[151px]")}
                        </div>
                        <div className={`flex-shrink-0 ${hideSecondMarket ? 'hidden min-[1650px]:block' : 'hidden min-[1500px]:block'}`}>
                          {renderMarketColCells(ev, topMarket3, "w-[151px]")}
                        </div>
                        {!hideSecondMarket && (
                          <>
                            <div className="hidden min-[1650px]:block flex-shrink-0">
                              {renderMarketColCells(ev, topMarket4, "w-[151px]")}
                            </div>
                            <div className="hidden min-[1750px]:block flex-shrink-0">
                              {renderMarketColCells(ev, topMarket5, "w-[151px]")}
                            </div>
                          </>
                        )}

                        <button onClick={() => handleEventClick(ev)} className="w-[52px] flex items-center justify-end text-[13px] text-gray-400 hover:text-gray-800 transition">
                          <svg className="w-3.5 h-3.5 mr-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M5 9h4v11H5zm6-5h4v16h-4zm6 8h4v8h-4z"/></svg>
                          {total} ›
                        </button>
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </React.Fragment>
          );
        })}

      </div>
    </div>
  );
};
